import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SettlementTable = ({ settlements, onSort, onBulkAction }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    const direction = sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    onSort(key, direction);
  };

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedRows(settlements?.map(s => s?.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => 
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { bg: 'bg-success/10', text: 'text-success', label: 'Selesai' },
      pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending' },
      failed: { bg: 'bg-error/10', text: 'text-error', label: 'Gagal' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-elevation-1 overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Detail Settlement Merchant</h2>
          {selectedRows?.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{selectedRows?.length} dipilih</span>
              <Button 
                variant="outline" 
                size="sm"
                iconName="Download"
                onClick={() => onBulkAction('export', selectedRows)}
              >
                Ekspor
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                iconName="RefreshCw"
                onClick={() => onBulkAction('reconcile', selectedRows)}
              >
                Rekonsiliasi
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input 
                  type="checkbox"
                  checked={selectedRows?.length === settlements?.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('merchantName')}
                  className="flex items-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  Nama Merchant
                  <SortIcon columnKey="merchantName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('settlementDate')}
                  className="flex items-center gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  Tanggal Settlement
                  <SortIcon columnKey="settlementDate" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button 
                  onClick={() => handleSort('grossAmount')}
                  className="flex items-center justify-end gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth w-full"
                >
                  Jumlah Bruto
                  <SortIcon columnKey="grossAmount" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button 
                  onClick={() => handleSort('fees')}
                  className="flex items-center justify-end gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth w-full"
                >
                  Biaya
                  <SortIcon columnKey="fees" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button 
                  onClick={() => handleSort('netAmount')}
                  className="flex items-center justify-end gap-2 text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-smooth w-full"
                >
                  Jumlah Neto
                  <SortIcon columnKey="netAmount" />
                </button>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-xs md:text-sm font-semibold text-foreground">Status</span>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-xs md:text-sm font-semibold text-foreground">Perbedaan</span>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-xs md:text-sm font-semibold text-foreground">Aksi</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {settlements?.map((settlement) => (
              <tr key={settlement?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4">
                  <input 
                    type="checkbox"
                    checked={selectedRows?.includes(settlement?.id)}
                    onChange={() => handleSelectRow(settlement?.id)}
                    className="w-4 h-4 rounded border-border"
                  />
                </td>
                <td className="px-4 py-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{settlement?.merchantName}</p>
                    <p className="text-xs text-muted-foreground">{settlement?.merchantId}</p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm text-foreground">{settlement?.settlementDate}</p>
                  <p className="text-xs text-muted-foreground">{settlement?.settlementTime}</p>
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="text-sm font-medium text-foreground data-text">{settlement?.grossAmount}</p>
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="text-sm text-error data-text">{settlement?.fees}</p>
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="text-sm font-semibold text-success data-text">{settlement?.netAmount}</p>
                </td>
                <td className="px-4 py-4 text-center">
                  {getStatusBadge(settlement?.status)}
                </td>
                <td className="px-4 py-4 text-center">
                  {settlement?.hasDiscrepancy ? (
                    <div className="flex items-center justify-center gap-1">
                      <Icon name="AlertTriangle" size={16} className="text-warning" />
                      <span className="text-xs text-warning font-medium">{settlement?.discrepancyAmount}</span>
                    </div>
                  ) : (
                    <Icon name="CheckCircle" size={16} className="text-success" />
                  )}
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button className="p-2 hover:bg-muted rounded-lg transition-smooth" title="Lihat Detail">
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button className="p-2 hover:bg-muted rounded-lg transition-smooth" title="Download">
                      <Icon name="Download" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Menampilkan {settlements?.length} dari {settlements?.length} settlement
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft">
            Sebelumnya
          </Button>
          <div className="flex items-center gap-1">
            <button className="px-3 py-1 rounded bg-primary text-primary-foreground text-sm font-medium">1</button>
            <button className="px-3 py-1 rounded hover:bg-muted text-sm font-medium text-foreground transition-smooth">2</button>
            <button className="px-3 py-1 rounded hover:bg-muted text-sm font-medium text-foreground transition-smooth">3</button>
          </div>
          <Button variant="outline" size="sm" iconName="ChevronRight" iconPosition="right">
            Selanjutnya
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettlementTable;